/**
 * The report's Armadillo conversation as a chat thread, as data in and data
 * out: each entry of the API's timeline becomes a bubble (an email either
 * way), a delivery line (files that arrived) or a system line (an order
 * sent, a quote recorded, an order closed).
 */

import type { ChatMessage } from '@/types/chat';
import {
  describeRegistryPrice,
  type RegistryConversationItem,
  type RegistryOrder,
  type RegistryOrderConversation,
  type RegistryOrderMessage,
} from '@/types/registry-documents';

/** The research desk, as the sender line of its bubbles names it. */
export const DESK_DISPLAY_NAME = 'Armadillo research desk';

/** What a bubble says for an email whose text was not kept. */
export const NO_TEXT_KEPT = 'No text was kept for this message - read it in the mailbox.';

/** "£70.00, 10 working days": what a recorded quote says, or null. */
function quoteTerms(item: RegistryConversationItem): string | null {
  const quote = item.quote;
  if (!quote) {
    return null;
  }
  const price = describeRegistryPrice(quote.price, quote.currency, false);
  const days = quote.turnaroundDays == null ? null : `${quote.turnaroundDays} working days`;
  return [price, days].filter(Boolean).join(', ') || null;
}

/** The sentence a system line carries: an order sent, a quote recorded, an
 * order closed. Never an email: those are bubbles of their own. */
export function conversationSystemText(item: RegistryConversationItem): string {
  if (item.kind === 'order_sent') {
    return `${item.reference} sent to Armadillo for ${item.companyName}`;
  }
  if (item.kind === 'quote_recorded') {
    const terms = quoteTerms(item);
    return `Quote recorded on ${item.reference}${terms ? `: ${terms}` : ''}`;
  }
  const ended = item.status === 'failed' ? 'failed' : 'closed';
  return `${item.reference} ${ended}${item.note ? `: ${item.note}` : ''}`;
}

/** An email from the desk that brought files. */
export function isDelivery(message: Pick<RegistryOrderMessage, 'direction' | 'newDocuments'>) {
  return message.direction === 'inbound' && message.newDocuments > 0;
}

/** What a delivery line says: "Armadillo delivered 2 files on GTI-…". */
export function deliveryText(
  item: Pick<RegistryConversationItem, 'reference'>,
  message: Pick<RegistryOrderMessage, 'newDocuments'>
): string {
  const files = message.newDocuments === 1 ? 'file' : 'files';
  return `Armadillo delivered ${message.newDocuments} ${files} on ${item.reference}`;
}

/**
 * The email as the chat bubble takes it. The desk's emails are the
 * partner's side, named for the desk with the address that wrote; the
 * Hub's replies are this side's, named for the colleague who sent them.
 * An email whose text was not kept says so in the bubble: a bubble with
 * no text at all would render nothing.
 */
export function chatMessageOf(
  item: Pick<RegistryConversationItem, 'orderId'>,
  message: RegistryOrderMessage,
  authorName: string | null
): ChatMessage {
  const outbound = message.direction === 'outbound';
  return {
    id: `${item.orderId}:${message.messageId ?? message.receivedAt ?? ''}`,
    chatThreadId: item.orderId,
    direction: message.direction,
    senderKind: outbound ? 'analyst' : 'np',
    senderUserId: message.sentByUserId,
    senderEmail: outbound ? '' : (message.sender?.trim() ?? ''),
    senderDisplayName: outbound ? authorName : DESK_DISPLAY_NAME,
    bodyText: message.text.trim() === '' ? NO_TEXT_KEPT : message.text,
    bodyHtml: null,
    isPrivate: false,
    freshdeskConversationId: null,
    deliveredAt: null,
    receivedAt: message.receivedAt,
    createdAt: null,
    attachments: [],
  };
}

/** One entry of the thread. */
export interface ConversationEntry {
  /** Entries carry no id of their own: the position names one. */
  key: string;
  item: RegistryConversationItem;
  /** The order the entry is on; undefined when the conversation no longer
   * lists it. */
  order: RegistryOrder | undefined;
  /** The email, for a message entry; null for a system line. */
  message: RegistryOrderMessage | null;
  /** The bubble, for an email with words of its own. A delivery with no
   * text is its delivery line alone. */
  chat: ChatMessage | null;
  /** The Hub's own reply: drawn on this side. Sides go by direction, never
   * by who is signed in: a colleague's reply is this side's too. */
  isMine: boolean;
  /** One of a run of bubbles by the same sender, which share one header. */
  groupedWithPrev: boolean;
}

/** Whether two emails read as one run: the same direction, and for the
 * Hub's replies the same colleague. */
function groupsWith(
  previous: ConversationEntry | undefined,
  message: RegistryOrderMessage
): boolean {
  const before = previous?.message;
  if (!before || previous.chat === null) {
    return false;
  }
  return before.direction === message.direction && before.sentByUserId === message.sentByUserId;
}

/** The thread, oldest first, as the API's timeline orders it. */
export function conversationEntries(
  conversation: RegistryOrderConversation,
  authorName: (userId: string | null) => string
): ConversationEntry[] {
  const orders = new Map(conversation.orders.map((order) => [order.id, order]));
  const entries: ConversationEntry[] = [];
  conversation.items.forEach((item, index) => {
    const key = `${item.kind}:${item.orderId}:${index}`;
    const order = orders.get(item.orderId);
    const message = item.kind === 'message' ? item.message : null;
    if (!message) {
      entries.push({
        key,
        item,
        order,
        message: null,
        chat: null,
        isMine: false,
        groupedWithPrev: false,
      });
      return;
    }
    const outbound = message.direction === 'outbound';
    const lineOnly = isDelivery(message) && message.text.trim() === '';
    const chat = lineOnly
      ? null
      : chatMessageOf(item, message, outbound ? authorName(message.sentByUserId) : null);
    entries.push({
      key,
      item,
      order,
      message,
      chat,
      isMine: outbound,
      groupedWithPrev: chat !== null && groupsWith(entries.at(-1), message),
    });
  });
  return entries;
}
